import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SnackbarService } from 'src/app/services/snackbar.service';
import {
  CreateEmailRequestDto,
  EmailService,
  UpdateEmailRequestDto,
} from 'src/generated-sources/openapi/alert-service/email';
import { MAX_PAGE_SIZE } from '../../constants';
import { ApiOperationHelper } from '../helpers/api-operation.helper';
import { AlertEmail } from 'src/app/services/models/alert-service/alert-email.model';
import { ProfileDataService } from 'src/app/services/data/profile-data.service';

@Injectable()
export class WalletEmailAlertConfigService {
  private readonly walletEmails$ = new BehaviorSubject<AlertEmail[]>([]);
  private readonly isLoadingEmailList$ = new BehaviorSubject<boolean>(false);
  private readonly actionIsLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly emailService: EmailService,
    private readonly profileDataService: ProfileDataService,
    private readonly snackbarService: SnackbarService,
  ) {}

  fetchWalletEmails(walletId: string) {
    const clientId = this.profileDataService.currentProfile?.clientId;

    ApiOperationHelper.executeOperation(
      {
        operation: () =>
          this.emailService.listPageable({
            xUserClientId: clientId,
            walletId: walletId,
            page: 0,
            perPage: MAX_PAGE_SIZE,
          }),
        onSuccess: (response) => {
          this.walletEmails$.next(response.items);
        },
        onError: () => {
          this.walletEmails$.next([]);
        },
        errorMessage: $localize`:@@js_getAlertEmailsListError:Error al intentar obtener e-mails`,
        loadingState: this.isLoadingEmailList$,
      },
      this.snackbarService,
    );
  }

  async createEmail(value: AlertEmail): Promise<AlertEmail | null> {
    const createEmailRequestDto: CreateEmailRequestDto = {
      walletId: value.walletId,
      email: value.email,
      position: value.position,
      status: true,
    };

    return ApiOperationHelper.executeAsyncOperation(
      {
        operation: () => this.emailService.create({ createEmailRequestDto }),
        onSuccess: (response) => {
          this.addWalletAlertEmailLocally(response);
        },
        errorMessage: $localize`:@@errorIncludingEmail:Error al incluir el e-mail`,
        loadingState: this.actionIsLoading$,
      },
      this.snackbarService,
    );
  }

  async updateEmail(value: AlertEmail): Promise<AlertEmail | null> {
    const updateEmailRequestDto: UpdateEmailRequestDto = {
      email: value.email,
      position: value.position,
      status: value.status,
    };

    return ApiOperationHelper.executeAsyncOperation(
      {
        operation: () => this.emailService.updateById({ id: value.id, updateEmailRequestDto }),
        onSuccess: (response) => {
          this.updateWalletAlertEmailLocally(value.id, response);
        },
        errorMessage: $localize`:@@errorEditingEmail:Error al cambiar el e-mail`,
        loadingState: this.actionIsLoading$,
      },
      this.snackbarService,
    );
  }

  deleteEmail(value: AlertEmail) {
    ApiOperationHelper.executeOperation(
      {
        operation: () => this.emailService.deleteById({ id: value.id }),
        onSuccess: () => {
          this.snackbarService.normal(
            $localize`:@@wasDeletedCorrectly:Se eliminó correctamente: ${value.email}`,
          );
          this.removeWalletAlertEmailLocally(value);
        },
        errorMessage: $localize`:@@ErrorTryingToExclude:Error al intentar eliminar`,
      },
      this.snackbarService,
    );
  }

  toggleEmailAlertStatus(walletEmail: AlertEmail, active: boolean) {
    const operation = () =>
      active ? this.enableEmail(walletEmail.id) : this.disableEmail(walletEmail.id);

    ApiOperationHelper.executeWithStateRollback(
      {
        operation,
        onSuccess: () => {
          this.updateWalletAlertEmailLocally(walletEmail.id, {
            status: active,
          });
        },
        rollbackAction: () => {
          this.updateWalletAlertEmailLocally(walletEmail.id, {
            status: !active,
          });
        },
        errorMessage: $localize`:@@ErrorTryingToEditStatus:Error al intentar cambiar el status`,
        loadingState: this.actionIsLoading$,
      },
      this.snackbarService,
    );
  }

  private enableEmail(emailId: string) {
    return this.emailService.enableById({ id: emailId });
  }

  private disableEmail(emailId: string) {
    return this.emailService.disableById({ id: emailId });
  }

  private addWalletAlertEmailLocally(value: AlertEmail) {
    const walletEmails = this.walletEmails$.getValue();
    this.walletEmails$.next([...walletEmails, value]);
  }

  private removeWalletAlertEmailLocally(value: AlertEmail) {
    const walletEmails = this.walletEmails$.getValue();
    const index = walletEmails.findIndex((email) => email.id == value.id);

    if (index < 0) return;

    const updatedEmails = walletEmails.filter((email) => email.id !== value.id);
    this.walletEmails$.next(updatedEmails);
  }

  private updateWalletAlertEmailLocally(walletEmailId: string, value: Partial<AlertEmail>) {
    const walletEmails = this.walletEmails$.getValue();
    const emailAlertIndex = walletEmails.findIndex(({ id }) => id === walletEmailId);

    if (emailAlertIndex < 0) return;

    const updatedEmails = walletEmails.map((email, index) => {
      if (index === emailAlertIndex) {
        return { ...email, ...value };
      }
      return email;
    });

    this.walletEmails$.next(updatedEmails);
  }

  getWalletEmails$() {
    return this.walletEmails$.asObservable();
  }

  getIsLoadingEmailList$() {
    return this.isLoadingEmailList$.asObservable();
  }

  getActionIsLoading$() {
    return this.actionIsLoading$.asObservable();
  }
}
